import React, { useEffect, useState } from "react";
import { apiInstanceFetch } from "../../util/api";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import { githubLight } from "@uiw/codemirror-theme-github";

const SITE_URL = "https://howyin.com";

// Build the live URL for a page slug (home = root).
const liveUrl = (slug) => (slug === "index" ? SITE_URL + "/" : `${SITE_URL}/${slug}`);

// Homepage numbers ({{S_KEY}} tokens injected at render time from the
// hidden "_stats" page — see webPage render controller).
const STAT_FIELDS = [
  ["USERS", "Active users"],
  ["SELLERS", "Sellers"],
  ["CREATORS", "Creators"],
  ["PROVIDERS", "Service providers"],
  ["FAITH", "Faith leaders"],
  ["COUNTRIES", "Countries"],
];

const Website = () => {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [stats, setStats] = useState(null);
  const [statsSaving, setStatsSaving] = useState(false);
  const [statsMsg, setStatsMsg] = useState("");

  useEffect(() => {
    loadList();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadList = async () => {
    try {
      const res = await apiInstanceFetch.get("webPage/adminList");
      if (res?.status && Array.isArray(res?.data)) {
        const visible = res.data.filter((p) => !p.slug.startsWith("_"));
        setPages(visible);
        if (visible.length) selectPage(visible[0].slug);
      }
    } catch (e) {}
  };

  const loadStats = async () => {
    try {
      const res = await apiInstanceFetch.get("webPage/adminGet?slug=_stats");
      if (res?.status && res?.data?.html) setStats(JSON.parse(res.data.html));
    } catch (e) {}
  };

  const saveStats = async () => {
    if (!stats) return;
    setStatsSaving(true);
    setStatsMsg("");
    try {
      const res = await apiInstanceFetch.patch("webPage/adminUpdate?slug=_stats", {
        html: JSON.stringify(stats, null, 2),
        title: "Site numbers",
      });
      setStatsMsg(res?.status ? "Numbers updated — live immediately ✓" : res?.message || "Save failed");
    } catch (e) {
      setStatsMsg("Save failed");
    } finally {
      setStatsSaving(false);
      setTimeout(() => setStatsMsg(""), 4000);
    }
  };

  const selectPage = async (slug) => {
    setLoading(true);
    setMessage("");
    try {
      const res = await apiInstanceFetch.get(
        `webPage/adminGet?slug=${encodeURIComponent(slug)}`
      );
      if (res?.status && res?.data) {
        setActiveSlug(slug);
        setTitle(res.data.title || "");
        setHtml(res.data.html || "");
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!activeSlug) return;
    setSaving(true);
    setMessage("");
    try {
      const res = await apiInstanceFetch.patch(
        `webPage/adminUpdate?slug=${encodeURIComponent(activeSlug)}`,
        { html, title }
      );
      setMessage(res?.status ? "Saved successfully ✓" : res?.message || "Save failed");
      loadList();
    } catch (e) {
      setMessage("Save failed");
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(""), 4000);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, color: "#1A1614" }}>Website</h4>
          <div style={{ fontSize: 13, color: "#8a8a8a" }}>
            Edit the live pages at{" "}
            <a href={SITE_URL} target="_blank" rel="noreferrer" style={{ color: "#C9A227", fontWeight: 600 }}>
              howyin.com
            </a>
          </div>
        </div>
      </div>

      {stats && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: "#1A1614", marginBottom: 4 }}>Site numbers</div>
          <div style={{ fontSize: 12.5, color: "#8a8a8a", marginBottom: 12 }}>
            The stats shown on the homepage (users, sellers, countries…). Edit and save — changes go live instantly, no rebuild needed.
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {STAT_FIELDS.map(([key, label]) => (
              <div key={key} style={{ minWidth: 130 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: "#9a9a9a", marginBottom: 4 }}>{label}</div>
                <input
                  value={stats[key] || ""}
                  onChange={(e) => setStats({ ...stats, [key]: e.target.value })}
                  style={{ width: "100%", padding: "8px 11px", border: "1px solid #e3e3e3", borderRadius: 8, fontSize: 14, fontWeight: 600 }}
                />
              </div>
            ))}
            <button
              onClick={saveStats}
              disabled={statsSaving}
              style={{ background: "#F0C420", color: "#1A1614", border: "none", padding: "9px 22px", borderRadius: 8, fontWeight: 700, cursor: statsSaving ? "default" : "pointer", opacity: statsSaving ? 0.7 : 1 }}
            >
              {statsSaving ? "Saving…" : "Save numbers"}
            </button>
            {statsMsg && (
              <span style={{ fontSize: 13, fontWeight: 600, color: statsMsg.includes("✓") ? "#2e9e4f" : "#c0392b" }}>{statsMsg}</span>
            )}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
        {/* Page list */}
        <div style={{ width: 230, background: "#fff", borderRadius: 12, padding: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          {pages.map((p) => (
            <div
              key={p.slug}
              onClick={() => selectPage(p.slug)}
              style={{
                padding: "11px 13px",
                borderRadius: 9,
                cursor: "pointer",
                marginBottom: 5,
                background: activeSlug === p.slug ? "#FFF8E1" : "transparent",
                border: activeSlug === p.slug ? "1px solid #F0C420" : "1px solid transparent",
                fontWeight: activeSlug === p.slug ? 600 : 500,
                color: "#1A1614",
              }}
            >
              <div style={{ fontSize: 14 }}>{p.title || p.slug}</div>
              <div style={{ fontSize: 11, color: "#9a9a9a" }}>/{p.slug === "index" ? "" : p.slug}</div>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div style={{ flex: 1, minWidth: 420, background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          {!activeSlug ? (
            <div style={{ color: "#9a9a9a", padding: 30, textAlign: "center" }}>Select a page to edit</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Page title"
                  style={{ flex: 1, minWidth: 200, padding: "9px 12px", border: "1px solid #e3e3e3", borderRadius: 8, fontSize: 14 }}
                />
                <a href={liveUrl(activeSlug)} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#C9A227", fontWeight: 600, whiteSpace: "nowrap" }}>
                  View live &#8599;
                </a>
              </div>

              <div style={{ fontSize: 12, color: "#9a9a9a", marginBottom: 6 }}>Page HTML</div>
              <div style={{ border: "1px solid #e3e3e3", borderRadius: 8, overflow: "hidden" }}>
                <CodeMirror
                  value={html}
                  height="62vh"
                  theme={githubLight}
                  extensions={[htmlLang()]}
                  editable={!loading}
                  onChange={(val) => setHtml(val)}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    autocompletion: true,
                    bracketMatching: true,
                    closeBrackets: true,
                  }}
                  style={{ fontSize: 13 }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12 }}>
                <button
                  onClick={save}
                  disabled={saving || loading}
                  style={{
                    background: "#1A1614",
                    color: "#fff",
                    border: "none",
                    padding: "10px 26px",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: saving ? "default" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
                {message && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: message.includes("success") ? "#2e9e4f" : "#c0392b" }}>
                    {message}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Website;
